/**
 * Adaptive capture guide for the NIN ID slip.
 *
 * A NIN slip is a long, narrow sheet whose proportions vary from print to
 * print, so a fixed on-screen frame cannot be filled on both axes: sized to
 * one slip it is either too wide or too tall for the next, and the
 * auto-capture check never passes. Instead, the guide takes the shape of the
 * slip the corner detector currently sees (side lengths of the detected quad)
 * and is fitted to the largest centered box of that shape that stays clear of
 * the instruction banner. The shape comes from the detection but the
 * position does not: the user still has to center the slip and fill the guide.
 *
 * Everything here is pure geometry in container pixels. The slip is always
 * held upright (long edge running up the screen), matching the 90° rotation
 * applied to the captured image (ROTATE_TYPES in DocumentCamera.jsx).
 */

export const NIN_SLIP_GUIDE = {
  // long edge / short edge of the slip as issued (8.5 : 4), used before any
  // slip is detected and whenever the measured shape is implausible
  defaultAspect: 8.5 / 4,
  // The slip is printed on A4 and usually cut out by hand, so its proportions
  // vary from copy to copy; the uncut A4 page (297/210 = 1.41) is also
  // accepted. Measured shapes outside this window are not a NIN slip (a
  // partly hidden slip, a table edge, a different document) and fall back to
  // the default.
  minAspect: 1.3,
  maxAspect: 2.6,
  // largest box the guide may occupy, as a fraction of the camera container.
  // The height cap keeps it clear of the banner at the top.
  maxWidthFraction: 0.9,
  maxHeightFraction: 0.8,
  // weight of the newest measurement when updating the guide shape
  smoothing: 0.5,
  // validation: the slip must fill this much of the guide on both axes, but
  // not overflow it, sit near its center, and stand close to upright
  minFill: 0.85,
  maxFill: 1.1,
  centerTolerance: 0.06,
  maxTiltDeg: 15,
};

const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

/**
 * Side lengths, centroid and tilt of a quad given as 4 corners in cyclic
 * order (any starting corner, either direction), in container pixels.
 * Opposite sides are averaged so perspective foreshortening and tilt do not
 * bias the measurement the way an axis-aligned bounding box would.
 * `tiltDeg` is the angle of the long side from vertical (0 = upright).
 */
export function measureQuad(corners) {
  const [c0, c1, c2, c3] = corners;
  const sideA = (dist(c0, c1) + dist(c2, c3)) / 2;
  const sideB = (dist(c1, c2) + dist(c3, c0)) / 2;

  // direction of the long sides, from one end to the other
  const longDir =
    sideA >= sideB
      ? { x: c1.x - c0.x + (c2.x - c3.x), y: c1.y - c0.y + (c2.y - c3.y) }
      : { x: c2.x - c1.x + (c3.x - c0.x), y: c2.y - c1.y + (c3.y - c0.y) };

  const longLen = Math.max(sideA, sideB);
  const shortLen = Math.min(sideA, sideB);
  return {
    longLen,
    shortLen,
    aspect: shortLen > 0 ? longLen / shortLen : Infinity,
    centroid: {
      x: (c0.x + c1.x + c2.x + c3.x) / 4,
      y: (c0.y + c1.y + c2.y + c3.y) / 4,
    },
    tiltDeg: (Math.atan2(Math.abs(longDir.x), Math.abs(longDir.y)) * 180) / Math.PI,
  };
}

/**
 * Next guide aspect from the previous one and a fresh measurement. Returns
 * null (use the default) when the measurement is not slip-shaped. The first
 * plausible measurement is taken as is; later ones are blended in so the
 * guide does not jitter with detector noise.
 */
export function smoothAspect(previous, measured) {
  const { minAspect, maxAspect, smoothing } = NIN_SLIP_GUIDE;
  if (!(measured >= minAspect && measured <= maxAspect)) return null;
  if (previous == null) return measured;
  return previous + smoothing * (measured - previous);
}

/**
 * The largest box of `aspect` (long/short, long edge vertical) that fits the
 * safe area of a `containerWidth` x `containerHeight` container, centered.
 * Pixel box, with width/height included for convenience.
 */
export function fitGuide(containerWidth, containerHeight, aspect = NIN_SLIP_GUIDE.defaultAspect) {
  const { maxWidthFraction, maxHeightFraction } = NIN_SLIP_GUIDE;
  const height = Math.min(maxHeightFraction * containerHeight, maxWidthFraction * containerWidth * aspect);
  const width = height / aspect;
  const x1 = (containerWidth - width) / 2;
  const y1 = (containerHeight - height) / 2;
  return { x1, y1, x2: x1 + width, y2: y1 + height, width, height };
}

/**
 * How well a measured slip fills `guide`. `reason` names the single most
 * useful correction for the user, in priority order, or null when it fits.
 */
export function evaluateFit(measure, guide, containerWidth, containerHeight, corners) {
  const { minFill, maxFill, centerTolerance, maxTiltDeg } = NIN_SLIP_GUIDE;

  const heightRatio = measure.longLen / guide.height;
  const widthRatio = measure.shortLen / guide.width;
  const upright = measure.tiltDeg <= maxTiltDeg;
  const clipped = corners.some((p) => p.x < 0 || p.y < 0 || p.x > containerWidth || p.y > containerHeight);
  const tooLarge = heightRatio > maxFill || widthRatio > maxFill;
  const sizeSufficient = heightRatio >= minFill && widthRatio >= minFill;
  const centered =
    Math.abs(measure.centroid.x - (guide.x1 + guide.x2) / 2) <= centerTolerance * containerWidth &&
    Math.abs(measure.centroid.y - (guide.y1 + guide.y2) / 2) <= centerTolerance * containerHeight;

  let reason = null;
  if (!upright) reason = "upright";
  else if (clipped || tooLarge) reason = "tooClose";
  else if (!sizeSufficient) reason = "tooFar";
  else if (!centered) reason = "offCenter";

  return { isValid: reason === null, reason, widthRatio, heightRatio };
}

/**
 * One detection step: measure the slip, update the guide shape and judge the
 * fit against that updated guide. `corners` are the detected corners in
 * displayed space (0..1 of the visible camera container).
 *
 * `aspect` in the result is the guide shape to carry into the next call
 * (null = default shape).
 */
export function checkNinSlipFit(corners, containerWidth, containerHeight, previousAspect) {
  const px = corners.map((p) => ({ x: p.x * containerWidth, y: p.y * containerHeight }));
  const measure = measureQuad(px);
  const aspect = smoothAspect(previousAspect, measure.aspect);
  const guide = fitGuide(containerWidth, containerHeight, aspect ?? NIN_SLIP_GUIDE.defaultAspect);
  return {
    aspect,
    guide,
    fit: evaluateFit(measure, guide, containerWidth, containerHeight, px),
  };
}
